import { haversineDistance } from '../../components/map/geo-utils.js'
import type { ProfilePoint } from '../../components/map/elevation-profile.js'

export function computeElevationStats(
  elevations: (number | null)[],
  deadband: number = 3
): { gain: number; loss: number; max: number; min: number } {
  let gain = 0
  let loss = 0
  let max = -Infinity
  let min = Infinity
  let committed: number | undefined

  for (const e of elevations) {
    if (e === null) continue
    if (e > max) max = e
    if (e < min) min = e

    if (committed === undefined) {
      committed = e
      continue
    }

    const delta = e - committed
    if (Math.abs(delta) > deadband) {
      if (delta > 0) gain += delta
      else loss += -delta
      committed = e
    }
  }

  if (max === -Infinity) return { gain: 0, loss: 0, max: 0, min: 0 }
  return { gain, loss, max, min }
}

export function computeElevationProfile(
  cumDistances: number[],
  elevations: (number | null)[]
): ProfilePoint[] {
  const points: ProfilePoint[] = []
  for (let i = 0; i < cumDistances.length; i++) {
    if (elevations[i] !== null) {
      points.push({ distance: cumDistances[i], elevation: elevations[i]! })
    }
  }
  return points
}

/**
 * Kalman filter fusing GPS and DEM elevation measurements.
 *
 * State: [elevation, rate_of_elevation_change]
 * Runs on cleaned track only (no rests/vehicle points).
 */
export function computeFusedElevations(
  coords: GeoJSON.Position[],
  demElevations: (number | null)[],
  accuracies: (number | null)[]
): number[] {
  const n = coords.length
  if (n === 0) return []

  // Initial state from first available GPS elevation
  const firstGpsElev = coords[0].length >= 3 ? coords[0][2] : 0
  let x = firstGpsElev // elevation estimate
  let v = 0 // rate of elevation change (m per meter of horizontal distance)

  // State covariance [P00, P01, P10, P11]
  let P00 = 100 // initial elevation uncertainty (m²)
  let P01 = 0
  let P10 = 0
  let P11 = 1 // initial rate uncertainty

  const DEM_VARIANCE = 64 // ~8m std dev for Terrarium/SRTM
  const DEFAULT_GPS_VARIANCE = 625 // ~25m std dev fallback when no accuracy

  const result: number[] = new Array(n)
  result[0] = x

  for (let i = 1; i < n; i++) {
    // Horizontal distance between consecutive cleaned points
    const dist = haversineDistance(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0])

    // --- Prediction step ---
    // Predict elevation: x += v * dist
    const xPred = x + v * dist
    const vPred = v

    // Process noise: max realistic grade ~50%, so elevation change per meter is ~0.5
    // Q scales with distance traveled
    const qElev = 0.25 * dist // (0.5)² * dist — variance grows with distance
    const qRate = 0.01 // rate can change slowly

    const P00Pred = P00 + dist * P10 + dist * P01 + dist * dist * P11 + qElev
    const P01Pred = P01 + dist * P11
    const P10Pred = P10 + dist * P11
    const P11Pred = P11 + qRate

    // --- GPS measurement update ---
    const gpsElev = coords[i].length >= 3 ? coords[i][2] : null
    let xUpd = xPred
    let vUpd = vPred
    let P00Upd = P00Pred
    let P01Upd = P01Pred
    let P10Upd = P10Pred
    let P11Upd = P11Pred

    if (gpsElev !== null) {
      const acc = accuracies[i]
      const gpsVariance = acc !== null ? acc * 2.5 * (acc * 2.5) : DEFAULT_GPS_VARIANCE

      // Kalman gain for elevation measurement
      const S = P00Upd + gpsVariance
      const K0 = P00Upd / S
      const K1 = P10Upd / S

      const innovation = gpsElev - xUpd
      xUpd = xUpd + K0 * innovation
      vUpd = vUpd + K1 * innovation

      P00Upd = (1 - K0) * P00Upd
      P01Upd = (1 - K0) * P01Upd
      P10Upd = P10Upd - K1 * P00Pred
      P11Upd = P11Upd - K1 * P01Pred
    }

    // --- DEM measurement update ---
    const demElev = demElevations[i]
    if (demElev !== null) {
      const S = P00Upd + DEM_VARIANCE
      const K0 = P00Upd / S
      const K1 = P10Upd / S

      const innovation = demElev - xUpd
      xUpd = xUpd + K0 * innovation
      vUpd = vUpd + K1 * innovation

      P00Upd = (1 - K0) * P00Upd
      P01Upd = (1 - K0) * P01Upd
      P10Upd = P10Upd - K1 * P00Upd
      P11Upd = P11Upd - K1 * P01Upd
    }

    x = xUpd
    v = vUpd
    P00 = P00Upd
    P01 = P01Upd
    P10 = P10Upd
    P11 = P11Upd

    result[i] = x
  }

  return result
}
